import { useCallback } from 'react'

import { noop } from 'lodash-es'

import { ProviderProgramTaxonomy } from '../../../models/providers/Providers'
import { YearProgramCoverage } from '../../../models/user/User'
import { DirectLinkIntent } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS, TREATMENT_OPTIONS_TYPE } from '../../common/constants/treatmentOptions'
import { isValidTreatmentOptionForDirectPath } from '../../common/utils/commonUtils'
import { getProgramFromTreatmentOption, shouldNavigateToHPIPage } from '../../healthPlan/utils'
import { getTriageTreatmentOptionFromCTPO } from '../../programConfigs/treatmentOptionProgramTaxonomyMappings'
import { DirectLinkSource } from '../constants'

interface useNavigateToDirectPathBookingArgs {
  setSelectedTreatmentOptionCTPO: (ctpo: ProviderProgramTaxonomy) => void
  setSelectedTreatmentOption: (treatmentOption?: TREATMENT_OPTIONS_TYPE) => void
  setDirectLinkUtmSource: (utm_source: string) => void
  setIsCurrentSearchDirectPath: (isDirectPath: boolean) => void
  setDirectLinkIntent: (directLinkIntent: string) => void
  setDirectLinkSource: (directLinkSource: DirectLinkSource) => void
  clearDirectPathState: () => void
  navigate: {
    goToHPIIneligible: () => void
    goToHPIPage: () => void
    goToLocationFinder: (entryPoint?: string) => void
    goToOnsiteLocationFinder: (entryPoint?: string) => void
    goToInternationalConsentForDataCollection: () => void
    goToTreatmentRecommendations: () => void
  }
}

/**
 * Hook that handles setting state variable needed for direct path navigation
 */
export const useNavigateToDirectPathBooking = ({
  setSelectedTreatmentOptionCTPO,
  setSelectedTreatmentOption,
  setIsCurrentSearchDirectPath,
  setDirectLinkIntent,
  setDirectLinkUtmSource,
  setDirectLinkSource,
  clearDirectPathState,
  navigate: {
    goToHPIPage = noop,
    goToHPIIneligible = noop,
    goToLocationFinder = noop,
    goToOnsiteLocationFinder = noop,
    goToInternationalConsentForDataCollection = noop,
    goToTreatmentRecommendations = noop,
  },
}: useNavigateToDirectPathBookingArgs) => {
  return useCallback(
    ({
      ctpo,
      treatmentOption,
      directLinkUtmSource,
      directLinkIntent,
      directLinkSource,
      isHealthPlanActive,
      getIsEAPSessionCountExhausted,
      currentProgramCoverageBreakdown,
      shouldSeeInternationalConsentForDataCollection,
      isInternationalUserWithDeclinedInternationalConsent,
      isUserInternational,
      forceShowHPIPage,
      entryPoint,
    }: {
      ctpo: Partial<ProviderProgramTaxonomy>
      treatmentOption?: string
      directLinkUtmSource?: string
      directLinkIntent?: string
      directLinkSource?: DirectLinkSource
      isHealthPlanActive: boolean
      forceShowHPIPage?: boolean
      getIsEAPSessionCountExhausted: (options: { treatmentOption?: TREATMENT_OPTIONS_TYPE | undefined }) => boolean
      currentProgramCoverageBreakdown: YearProgramCoverage
      shouldSeeInternationalConsentForDataCollection: boolean
      isInternationalUserWithDeclinedInternationalConsent: boolean
      isUserInternational: boolean
      entryPoint?: string
    }) => {
      clearDirectPathState()
      setIsCurrentSearchDirectPath(true)
      const ctpoTreatmentOption = getTriageTreatmentOptionFromCTPO(ctpo as ProviderProgramTaxonomy)
      const finalTreatmentOption = ctpoTreatmentOption ?? (treatmentOption as TREATMENT_OPTIONS_TYPE)
      if (ctpoTreatmentOption) {
        setSelectedTreatmentOptionCTPO(ctpo as ProviderProgramTaxonomy)
      }
      if (isValidTreatmentOptionForDirectPath(finalTreatmentOption)) {
        setSelectedTreatmentOption(finalTreatmentOption)
      }
      if (directLinkIntent) {
        setDirectLinkIntent(directLinkIntent)
      }
      if (directLinkSource) {
        setDirectLinkSource(directLinkSource)
      }
      if (directLinkUtmSource) {
        setDirectLinkUtmSource(directLinkUtmSource)
      }

      const isEAPSessionCountExhausted = getIsEAPSessionCountExhausted({ treatmentOption: finalTreatmentOption })
      if (shouldSeeInternationalConsentForDataCollection) {
        return goToInternationalConsentForDataCollection()
      } else if (isInternationalUserWithDeclinedInternationalConsent) {
        return goToTreatmentRecommendations() // todo: match behavior when going through s4c triage. start a shared hook?
        // OUS onsite therapy: forcibly skip the eligibility guards below because
        // getProgramFromTreatmentOption resolves individualTherapy to LCT
        // (`considerLyraTherapy`), which OUS customers don't have.
        //
        // OUS users don't have currently have HPI coverage, so skipping this is safe for now.
        // US users will qualify for the normal HPI guards since their customers will have `considerLyraTherapy`.
        // TODO: remove this clause when `getProgramFromTreatmentOption` appropriately resolves to a single given program,
        // versus the _first_ returned program.
      } else if (directLinkIntent === DirectLinkIntent.ONSITE_THERAPY && isUserInternational) {
        return goToOnsiteLocationFinder(entryPoint)
        // This is a temporary solution (similar to the one above) to workaround getProgramFromTreatmentOption resolving individualTherapy to LCT
        // instead of directAccessTherapy for OUS users, which results in no matching program (see https://github.com/LyraHealth/ui-monorepo/pull/17444)
      } else if (
        directLinkIntent === DirectLinkIntent.COVERAGE_INFORMATION &&
        finalTreatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY &&
        isUserInternational
      ) {
        return goToLocationFinder(entryPoint)
      } else if (
        !!finalTreatmentOption &&
        !getProgramFromTreatmentOption({
          treatmentOptionName: finalTreatmentOption ?? null,
          customerPrograms: currentProgramCoverageBreakdown,
          allowPartialTreatmentOptions: true,
        })
      ) {
        return goToHPIIneligible()
      } else if (
        forceShowHPIPage ||
        shouldNavigateToHPIPage({
          treatmentOption: finalTreatmentOption,
          customerPrograms: currentProgramCoverageBreakdown,
          isHealthPlanActive,
          isEAPSessionCountExhausted,
          allowPartialTreatmentOptions: true,
        })
      ) {
        return goToHPIPage()
      }

      if (directLinkIntent === DirectLinkIntent.ONSITE_THERAPY) {
        return goToOnsiteLocationFinder(entryPoint)
      }
      return goToLocationFinder(entryPoint)
    },
    [
      clearDirectPathState,
      goToHPIIneligible,
      goToHPIPage,
      goToInternationalConsentForDataCollection,
      goToLocationFinder,
      goToOnsiteLocationFinder,
      goToTreatmentRecommendations,
      setDirectLinkIntent,
      setDirectLinkSource,
      setIsCurrentSearchDirectPath,
      setSelectedTreatmentOption,
      setSelectedTreatmentOptionCTPO,
      setDirectLinkUtmSource,
    ],
  )
}
